import { useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { UploadCloud, FileSpreadsheet, RefreshCcw } from "lucide-react";

const BASE_URL = "http://nissi-india.com";

const UploadExcel = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState(null);
  const [errors, setErrors] = useState([]);

  const screenBadge = useMemo(() => "INFRA SCREEN : Upload Excel", []);

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const handleReset = () => {
    setFile(null);
    setSummary(null);
    setErrors([]);
  };

  const handleUpload = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!file) {
      toast.warn("Please choose Excel file.");
      return;
    }

    try {
      setLoading(true);
      setSummary(null);
      setErrors([]);

      const fd = new FormData();
      fd.append("file", file);

      const res = await axios.post(`${BASE_URL}/api/upload-excel/employee-master`, fd, {
        params: { finYearId: Number(finYearId) },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });

      if (res?.data?.success) {
        setSummary(res.data.summary || null);
        setErrors(res.data.errors || []);
        toast.success(res.data.message || "Processed successfully");
      } else {
        setSummary(res?.data?.summary || null);
        setErrors(res?.data?.errors || []);
        toast.error(res?.data?.message || "Upload failed");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
  if (!String(finYearId).trim()) {
    toast.warn("Please select Financial Year.");
    return;
  }
  // Simple direct download
  window.open(`${BASE_URL}/api/upload-excel/employee-master/template?finYearId=${Number(finYearId)}`, "_blank");
};


  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">UPLOAD EXCEL</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-6xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <FileSpreadsheet className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Upload Excel to update Employee Master
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Sheet name must be <b>EmployeeDetails</b>
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={handleReset} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reset
              </button>
            </div>
          </div>

          {/* Upload Card */}
          <div className={cardBase + " p-6"}>
            <label className={labelBase}>Excel File</label>
            <div className="mt-2 flex flex-col md:flex-row items-start md:items-center gap-4">
              <input
                type="file"
                accept=".xls,.xlsx"
                className={inputBase}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />

             
 
              <button onClick={handleUpload} className={primaryBtn} disabled={loading}>
                <UploadCloud className="w-5 h-5" />
                {loading ? "Uploading..." : "Submit"}
              </button>

               <button onClick={downloadTemplate} className={ghostBtn} disabled={loading}>
  <FileSpreadsheet className="w-5 h-5" />
  Template
</button>
            </div>

            {file && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Selected: <b>{file.name}</b>
              </div>
            )}
          </div>

          {/* Summary */}
          {summary && (
            <div className={cardBase + " p-6 mt-6"}>
              <div className="text-lg font-extrabold mb-4">Result Summary</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div className={`p-4 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-50"}`}>
                  <div className="font-bold">Direct Updated</div>
                  <div className="text-2xl font-extrabold">{summary.updatedDirect ?? 0}</div>
                </div>
                <div className={`p-4 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-50"}`}>
                  <div className="font-bold">Sent To Approval</div>
                  <div className="text-2xl font-extrabold">{summary.sentToApproval ?? 0}</div>
                </div>
                <div className={`p-4 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-50"}`}>
                  <div className="font-bold">Skipped</div>
                  <div className="text-2xl font-extrabold">{summary.skipped ?? 0}</div>
                </div>
                <div className={`p-4 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-50"}`}>
                  <div className="font-bold">Errors</div>
                  <div className="text-2xl font-extrabold">{summary.errorCount ?? 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* Errors Table */}
          <div className={cardBase + " mt-6"}>
            <div className="p-5 border-b border-gray-200 dark:border-white/10">
              <div className="text-lg font-extrabold">Excel Errors</div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Fix these rows in Excel and upload again.
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-bold">Row No</th>
                    <th className="px-4 py-3 text-center font-bold">Field</th>
                    <th className="px-4 py-3 text-left font-bold">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {errors.length === 0 ? (
                    <tr>
                      <td colSpan={3} className={`px-4 py-8 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Errors
                      </td>
                    </tr>
                  ) : (
                    errors.map((e, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {e.rowNo}
                        </td>
                        <td className={`px-4 py-3 text-center text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {e.field}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {e.message}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default UploadExcel;
